import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Typography, Button, Drawer, Popover, Badge, Tag, List, Empty, Modal, Dropdown, Form, Input, Switch } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import Notifications from '../pages/notifications/notifications/Notifications';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  BellOutlined,
  LogoutOutlined,
  SettingOutlined,
  LockOutlined,
  CheckSquareOutlined,
  FileDoneOutlined,
  WarningOutlined,
  AccountBookOutlined,
  BugOutlined,
  ToolOutlined,
  BookOutlined,
} from '@ant-design/icons';
import './MainLayout.css';
import ReadOnlyField from '../components/readOnlyField/ReadOnlyField';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { removeAuth, setUserDetail } from '../redux/auth.reducer';
import { setMessage } from '../redux/message.reducer';
import { usePWA } from '../pwa/PWAContext';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export const NewOrderModalContext = React.createContext(null);

const SIDEBAR_WIDTH = 200;
const SIDEBAR_COLLAPSED_WIDTH = 80;

const baseNavItems = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: '/orders', icon: <ShoppingOutlined />, label: 'Orders' },
  { key: '/orders/action-required', icon: <WarningOutlined />, label: 'Action Required' },
  { key: '/bulk-orders', icon: <ShoppingCartOutlined />, label: 'Bulk Orders' },
];

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user } = useSelector((state) => state.authReducer);
  const {
    notificationPermission,
    notificationSoundEnabled,
    setNotificationSoundEnabled,
    playNotificationSound,
    requestNotificationPermission,
    subscribeToPush,
    swRegistration,
  } = usePWA();

  const isAdmin = user?.roles?.some((r) => r.value === 'admin');
  const isAccounting = user?.roles?.some((r) => r.value === 'accounting');
  const isDeveloper = user?.roles?.some((r) => r.value === 'developer');
  const navItems = [
    ...baseNavItems,
    ...(!isAdmin ? [{ key: '/my-tasks', icon: <CheckSquareOutlined />, label: 'My Tasks' }] : []),
    ...(isAdmin ? [{ key: '/users', icon: <UserOutlined />, label: 'Users' }] : []),
    ...(isAdmin ? [{ key: '/tasks', icon: <CheckSquareOutlined />, label: 'Tasks' }] : []),
    ...(isAdmin ? [{ key: '/task-completions', icon: <FileDoneOutlined />, label: 'Completions' }] : []),
    { key: '/repairs', icon: <ToolOutlined />, label: 'Repairs' },
    // { key: '/encyclopedia', icon: <BookOutlined />, label: 'Encyclopedia' },
    ...(isAdmin || isAccounting ? [{ key: '/etsy-accounting', icon: <AccountBookOutlined />, label: 'Etsy Accounting' }] : []),
    ...(isDeveloper ? [{ key: '/errors', icon: <BugOutlined />, label: 'Error Logs' }] : []),
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const performLogout = () => {
    dispatch(removeAuth());
    navigate('/login');
  };

  const handleLogoutClick = () => {
    setLogoutModalOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutLoading(true);
    axios
      .post('/auth/logout')
      .then(() => performLogout())
      .catch(() => performLogout())
      .finally(() => {
        setLogoutLoading(false);
        setLogoutModalOpen(false);
      });
  };

  const openAccountModal = () => {
    accountForm.setFieldsValue({
      name: user?.name ?? '',
      email: user?.email ?? '',
    });
    setAccountModalOpen(true);
  };

  const handleAccountSave = async () => {
    try {
      const values = await accountForm.validateFields();
      setAccountSaveLoading(true);
      const res = await axios.patch('/users/me', {
        name: values.name?.trim() ?? '',
      });
      if (res.data?.user) {
        dispatch(setUserDetail({
          email: res.data.user.email,
          name: res.data.user.name,
          roles: res.data.user.roles || [],
        }));
      }
      dispatch(setMessage({ message: 'Profile updated', type: 'success' }));
      setAccountModalOpen(false);
    } catch (err) {
      if (err.errorFields) return;
      const msg = err.response?.data?.error || 'Update failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setAccountSaveLoading(false);
    }
  };

  const openChangePasswordModal = () => {
    changePasswordForm.resetFields();
    setChangePasswordModalOpen(true);
  };

  const handleChangePasswordSubmit = async () => {
    try {
      const values = await changePasswordForm.validateFields();
      setChangePasswordLoading(true);
      await axios.put('/users/me/password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword.trim(),
      });
      dispatch(setMessage({ message: 'Password changed successfully', type: 'success' }));
      setChangePasswordModalOpen(false);
    } catch (err) {
      if (err.errorFields) return;
      const msg = err.response?.data?.error || 'Change password failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const userMenuItems = [
    { key: 'account', label: 'My Account', icon: <SettingOutlined />, onClick: openAccountModal },
    { key: 'changePassword', label: 'Change password', icon: <LockOutlined />, onClick: openChangePasswordModal },
    { type: 'divider' },
    { key: 'logout', label: 'Logout', icon: <LogoutOutlined />, danger: true, onClick: handleLogoutClick },
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [openKeys, setOpenKeys] = useState(['orders']);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [accountSaveLoading, setAccountSaveLoading] = useState(false);
  const [accountForm] = Form.useForm();
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const refetchOrdersRef = useRef(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false);
  const [pushSubscribing, setPushSubscribing] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith('/orders')) {
      setOpenKeys((k) => (k.includes('orders') ? k : [...k, 'orders']));
    }
  }, [location.pathname]);

  const handleNavClick = ({ key }) => {
    if (key.startsWith('/')) {
      navigate(key);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleCollapsedNavClick = (key) => {
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  const getSelectedKey = () => {
    if (location.pathname.startsWith('/task-completions')) return '/task-completions';
    if (location.pathname.startsWith('/repairs')) return '/repairs';
    if (location.pathname.startsWith('/encyclopedia')) return '/encyclopedia';
    if (location.pathname.startsWith('/my-tasks') || location.pathname.startsWith('/my-completions') || location.pathname.startsWith('/my-rewards')) return '/my-tasks';
    if (location.pathname.startsWith('/users')) return '/users';
    if (location.pathname.startsWith('/tasks')) return '/tasks';
    if (location.pathname.startsWith('/etsy-accounting')) return '/etsy-accounting';
    if (location.pathname.startsWith('/errors')) return '/errors';
    return location.pathname;
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const isSidebarCollapsed = !isMobile && collapsed;

  const sidebarContent = (
    <>
      <div className="main-layout-logo">
        <img src={`${process.env.PUBLIC_URL || ''}/KOSHA-logo.png`} alt="Kosha" className="main-layout-logo-icon main-layout-logo-img" />
        {(!collapsed || isMobile) && (
          <Text strong className="main-layout-logo-text">
            Kosha
          </Text>
        )}
      </div>
      {isSidebarCollapsed ? (
        <nav className="main-layout-collapsed-nav" aria-label="Sidebar navigation">
          {navItems.map((item) => {
            const selected = getSelectedKey() === item.key;
            return (
              <button
                key={item.key}
                type="button"
                className={`main-layout-collapsed-nav-item${selected ? ' main-layout-collapsed-nav-item-selected' : ''}`}
                onClick={() => handleCollapsedNavClick(item.key)}
                title={item.label}
                aria-label={item.label}
                aria-current={selected ? 'page' : undefined}
              >
                <span className="main-layout-collapsed-nav-icon">{item.icon}</span>
              </button>
            );
          })}
        </nav>
      ) : (
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={navItems}
          onClick={handleNavClick}
          className="main-layout-menu"
        />
      )}
    </>
  );

  const openNewOrderModal = () => navigate('/orders/new');

  return (
    <NewOrderModalContext.Provider value={{ openNewOrderModal, registerRefetchOrders: (fn) => { refetchOrdersRef.current = fn; } }}>
    <Layout className="main-layout">
      {/* Desktop sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={SIDEBAR_WIDTH}
          collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
          className="main-layout-sider"
        >
          {sidebarContent}
        </Sider>
      )}

      {/* Mobile sidebar drawer */}
      {isMobile && (
        <Drawer
          title={
            <div className="main-layout-drawer-title-wrap">
              <img src={`${process.env.PUBLIC_URL || ''}/KOSHA-logo.png`} alt="Kosha" className="main-layout-logo-icon main-layout-logo-img main-layout-drawer-logo-icon" />
              <Text strong className="main-layout-drawer-title">
                Kosha
              </Text>
            </div>
          }
          placement="left"
          onClose={() => setMobileOpen(false)}
          open={mobileOpen}
          size={SIDEBAR_WIDTH}
          className="main-layout-drawer"
          styles={{
            header: { borderBottom: '1px solid #f0f0f0' },
            body: { padding: 0 },
          }}
        >
          <div className="main-layout-drawer-content">
            <div className="main-layout-nav-label">Navigation</div>
            <Menu
              theme="light"
              mode="inline"
              selectedKeys={[
                location.pathname.startsWith('/task-completions')
                  ? '/task-completions'
                  : location.pathname.startsWith('/repairs')
                    ? '/repairs'
                    : location.pathname.startsWith('/encyclopedia')
                      ? '/encyclopedia'
                      : location.pathname.startsWith('/my-tasks') ||
                        location.pathname.startsWith('/my-completions') ||
                        location.pathname.startsWith('/my-rewards')
                      ? '/my-tasks'
                      : location.pathname.startsWith('/users')
                        ? '/users'
                        : location.pathname.startsWith('/tasks')
                          ? '/tasks'
                          : location.pathname,
              ]}
              openKeys={openKeys}
              onOpenChange={handleOpenChange}
              items={navItems}
              onClick={handleNavClick}
              className="main-layout-menu main-layout-menu-drawer"
              style={{ borderRight: 0 }}
            />
          </div>
        </Drawer>
      )}

      <Layout className="main-layout-right">
        <Header className="main-layout-header">
          <Button
            type="text"
            htmlType="button"
            icon={
              isMobile
                ? (mobileOpen ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)
                : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)
            }
            onClick={() =>
              isMobile ? setMobileOpen(!mobileOpen) : setCollapsed(!collapsed)
            }
            className="main-layout-trigger"
            aria-label={isMobile ? 'Toggle menu' : 'Toggle sidebar'}
          />
          <div className="main-layout-header-actions">
            {user?.roles?.length > 0 && (
              <span className="main-layout-roles">
                {user.roles.map((role) => (
                  <Tag key={role.value ?? role.label} color="blue">
                    {role.label}
                  </Tag>
                ))}
              </span>
            )}
            <Popover
              overlayClassName="main-layout-notifications-popover"
              placement={isMobile ? 'bottom' : 'bottomRight'}
              content={
                <div className="main-layout-notifications-panel">
                  <div className="main-layout-notifications-title">Notifications</div>
                  {user?.notificationsList?.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No new notifications"
                      style={{ margin: '12px 0' }}
                    />
                  ) : (
                    <List
                      size="small"
                      dataSource={user?.notificationsList || []}
                      renderItem={(item) => (
                        <List.Item>
                          <div className="main-layout-notification-item">
                            <div className="main-layout-notification-item-title">
                              {item.title}
                            </div>
                          </div>
                        </List.Item>
                      )}
                      style={{ maxHeight: 320, overflow: 'auto' }}
                    />
                  )}
                  {user?.notificationsList?.length > 0 && (
                    <div className="main-layout-notifications-footer">
                      <button
                        type="button"
                        onClick={() => {
                          setNotificationsOpen(false);
                          setNotificationsModalOpen(true);
                        }}
                        className="main-layout-notifications-show-all"
                      >
                        Show all notifications
                      </button>
                    </div>
                  )}
                </div>
              }
              title={null}
              trigger="click"
              open={notificationsOpen}
              onOpenChange={setNotificationsOpen}
            >
              <Badge count={user?.unreadNotifications || 0} size="small" offset={[-2, 2]}>
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  className="main-layout-notification-btn"
                  aria-label="Notifications"
                />
              </Badge>
            </Popover>
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
              <Button
                type="text"
                icon={<UserOutlined />}
                className="main-layout-user-btn"
                aria-label="User menu"
              />
            </Dropdown>
          </div>
        </Header>
        <Content className="main-layout-content">
          <Outlet />
        </Content>
      </Layout>

      <Modal
        title="My Account"
        open={accountModalOpen}
        onCancel={() => setAccountModalOpen(false)}
        footer={[
          <Button
            key="save"
            type="primary"
            onClick={() => accountForm.submit()}
            loading={accountSaveLoading}
            className="app-btn-primary"
          >
            Save
          </Button>,
          <Button key="cancel" onClick={() => setAccountModalOpen(false)} className="app-btn-cancel">
            Cancel
          </Button>,
        ]}
        destroyOnClose
        width={420}
        className="main-layout-account-modal"
      >
        <Form
          form={accountForm}
          layout="vertical"
          onFinish={handleAccountSave}
          initialValues={{
            name: user?.name ?? '',
            email: user?.email ?? '',
          }}
        >
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="Your name" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <ReadOnlyField placeholder="Email" />
          </Form.Item>
          <Form.Item label="Role">
            <div className="main-layout-account-roles">
              {user?.roles?.map((role) => (
                <Tag key={role.value || role}>{role.label ?? role.value ?? role}</Tag>
              )) ?? '—'}
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Change password"
        open={changePasswordModalOpen}
        onCancel={() => setChangePasswordModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setChangePasswordModalOpen(false)} className="app-btn-cancel">
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => changePasswordForm.submit()}
            loading={changePasswordLoading}
            className="app-btn-primary"
          >
            Update password
          </Button>,
        ]}
        destroyOnClose
        width={420}
        className="main-layout-account-modal"
      >
        <Form
          form={changePasswordForm}
          layout="vertical"
          onFinish={handleChangePasswordSubmit}
          initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
        >
          <Form.Item
            name="currentPassword"
            label="Current password"
            rules={[{ required: true, message: 'Enter your current password' }]}
          >
            <Input.Password placeholder="••••••••" autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New password"
            rules={[
              { required: true, message: 'Enter new password' },
              { min: 6, message: 'At least 6 characters' },
            ]}
          >
            <Input.Password placeholder="••••••••" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm new password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Confirm new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="••••••••" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="All notifications"
        open={notificationsModalOpen}
        onCancel={() => setNotificationsModalOpen(false)}
        footer={null}
        width={isMobile ? '100%' : 720}
        style={isMobile ? { maxWidth: 'calc(100vw - 32px)', top: 16 } : undefined}
        destroyOnClose
        className="main-layout-notifications-modal"
      >
        <Notifications />
      </Modal>

      <Modal
        title="Logout"
        open={logoutModalOpen}
        onCancel={() => setLogoutModalOpen(false)}
        footer={[
          <Button
            key="logout"
            danger
            onClick={handleLogoutConfirm}
            loading={logoutLoading}
            className="app-btn-delete"
          >
            Logout
          </Button>,
          <Button key="cancel" onClick={() => setLogoutModalOpen(false)} className="app-btn-cancel">
            Cancel
          </Button>,
        ]}
        destroyOnClose
      >
        Are you sure you want to logout?
      </Modal>
    </Layout>
    </NewOrderModalContext.Provider>
  );
}
